import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components';
import './Auth.css';

type PageStatus = 'loading' | 'ready' | 'success' | 'error' | 'already-set';

export function SetPassword() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();

  const [status, setStatus] = useState<PageStatus>('loading');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const token = searchParams.get('token');
  const isRTL = i18n.language === 'fa';

  // Verify token on load
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError(isRTL ? 'لینک نامعتبر است' : 'Invalid link');
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch('/api/auth/setup/verify-token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (data.success) {
          if (data.data.passwordSet) {
            setStatus('already-set');
            setUserEmail(data.data.email);
          } else {
            setStatus('ready');
            setUserEmail(data.data.email);
          }
        } else {
          setStatus('error');
          setError(data.error?.message_fa || data.error?.message || 'Invalid or expired link');
        }
      } catch {
        setStatus('error');
        setError(isRTL ? 'خطا در اتصال به سرور' : 'Connection error');
      }
    };

    verifyToken();
  }, [token, isRTL]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords
    if (password.length < 8) {
      setError(isRTL ? 'رمز عبور باید حداقل ۸ کاراکتر باشد' : 'Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError(isRTL ? 'رمز عبور و تکرار آن مطابقت ندارند' : 'Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/auth/setup/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (data.success) {
        // Auto-login with the returned tokens
        auth.setAuthData({
          user: data.data.user,
          accessToken: data.data.accessToken,
          refreshToken: data.data.refreshToken,
        });

        setStatus('success');

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        setError(data.error?.message_fa || data.error?.message || 'Failed to set password');
      }
    } catch {
      setError(isRTL ? 'خطا در ذخیره رمز عبور' : 'Failed to save password');
    }

    setIsSubmitting(false);
  };

  return (
    <div className={`auth-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <img src="/logo-header.png" alt="SolarMaktab" />
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="auth-card">
          {status === 'loading' && (
            <div className="setup-status">
              <div className="setup-spinner"></div>
              <h2>{isRTL ? 'در حال بررسی...' : 'Verifying...'}</h2>
            </div>
          )}

          {status === 'ready' && (
            <>
              <h1 className="auth-title">
                {isRTL ? '🎉 خوش آمدید!' : '🎉 Welcome!'}
              </h1>
              <p className="setup-intro">
                {isRTL 
                  ? `حساب شما (${userEmail}) تأیید شد. لطفاً یک رمز عبور برای خود انتخاب کنید.`
                  : `Your account (${userEmail}) is verified. Please set a password.`}
              </p>

              {error && <div className="auth-error">{error}</div>}

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label htmlFor="password">
                    {isRTL ? 'رمز عبور' : 'Password'}
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isRTL ? 'حداقل ۸ کاراکتر' : 'At least 8 characters'}
                    required
                    minLength={8}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    {isRTL ? 'تکرار رمز عبور' : 'Confirm Password'}
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={isRTL ? 'رمز عبور را دوباره وارد کنید' : 'Enter password again'}
                    required
                    minLength={8}
                    disabled={isSubmitting}
                    autoComplete="new-password"
                  />
                </div>

                <button
                  type="submit"
                  className="auth-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting 
                    ? (isRTL ? 'در حال ذخیره...' : 'Saving...') 
                    : (isRTL ? 'ذخیره و ورود' : 'Save & Login')}
                </button>
              </form>
            </>
          )}

          {status === 'success' && (
            <div className="setup-status success">
              <div className="setup-icon">✓</div>
              <h2>{isRTL ? 'حساب شما فعال شد!' : 'Account Activated!'}</h2>
              <p>{isRTL ? 'در حال انتقال به داشبورد...' : 'Redirecting to dashboard...'}</p>
            </div>
          )}

          {status === 'already-set' && (
            <div className="setup-status info">
              <div className="setup-icon info">ℹ️</div>
              <h2>{isRTL ? 'حساب قبلاً فعال شده' : 'Already Activated'}</h2>
              <p>
                {isRTL 
                  ? `حساب ${userEmail} قبلاً فعال شده است. لطفاً وارد شوید.`
                  : `Account ${userEmail} is already activated. Please log in.`}
              </p>
              <Link to="/login" className="auth-submit-btn" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '1rem' }}>
                {isRTL ? 'ورود' : 'Login'}
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="setup-status error">
              <div className="setup-icon error">✕</div>
              <h2>{isRTL ? 'خطا' : 'Error'}</h2>
              <p className="setup-error">{error}</p>
              <Link to="/" className="auth-submit-btn" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '1rem' }}>
                {isRTL ? 'بازگشت به صفحه اصلی' : 'Back to Home'}
              </Link>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .setup-status {
          text-align: center;
          padding: 2rem 0;
        }
        .setup-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e5e7eb;
          border-top-color: #2563eb;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .setup-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: bold;
          margin: 0 auto 1rem;
          background: #10b981;
          color: white;
        }
        .setup-icon.error {
          background: #ef4444;
        }
        .setup-icon.info {
          background: #3b82f6;
        }
        .setup-status.success h2 {
          color: #10b981;
        }
        .setup-status.error h2 {
          color: #ef4444;
        }
        .setup-intro {
          text-align: center;
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
          line-height: 1.6;
        }
        .setup-error {
          color: #6b7280;
        }
      `}</style>
    </div>
  );
}
